import React from "react";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * ScanAuditOverviewCard — Sub-sprint D-β-α.
 *
 * Renders the `/scan-audit-overview` aggregate response (3 time
 * windows + grouped top-N lists + pre-lookup denials + health
 * indicators). Read-only.
 */
const Stat = ({ label, value, testId, tone = "slate" }) => {
  const cls = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-rose-200 bg-rose-50 text-rose-800",
  }[tone];
  return (
    <div data-testid={testId} className={`flex flex-1 flex-col items-start gap-0.5 rounded-md border px-3 py-2 ${cls}`}>
      <span className="text-[10px] font-medium uppercase tracking-wide">{label}</span>
      <span className="font-mono text-base font-semibold">{value ?? 0}</span>
    </div>
  );
};

const TopList = ({ title, items, testId }) => (
  <div data-testid={testId} className="flex flex-col gap-1">
    <span className="text-[11px] font-medium text-slate-700">{title}</span>
    {(!items || items.length === 0) ? (
      <span className="text-[11px] text-slate-400">—</span>
    ) : (
      <ul className="flex flex-col gap-0.5">
        {items.slice(0, 10).map((entry, i) => (
          <li key={i} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1 text-[11px]">
            <code className="truncate font-mono text-slate-700">{String(entry.value ?? "(null)").slice(0, 40)}</code>
            <span className="rounded bg-slate-200 px-1.5 py-0.5 font-mono text-slate-700">{entry.count}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const ScanAuditOverviewCard = ({ data, dataTestId = "rl-ops-scan-audit-card" }) => {
  const t = useRacketLabAdminT();
  if (!data) {
    return (
      <div data-testid={`${dataTestId}-empty`} className="rounded-md border border-dashed border-slate-300 bg-white p-4 text-xs text-slate-500">
        {t("ops.scan_audit.empty")}
      </div>
    );
  }
  const w = data.windows || {};
  const a = data.audit || {};
  const p = data.pre_lookup || {};
  const h = data.health || {};
  return (
    <div data-testid={dataTestId} className="flex flex-col gap-4 rounded-md border border-slate-200 bg-white p-4">
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-slate-800">{t("ops.scan_audit.card_title")}</h2>
        <span className="text-[10px] text-slate-500">{t("ops.scan_audit.generated_at_label")} <code>{data.generated_at}</code></span>
      </header>

      <section>
        <p className="mb-1 text-[11px] font-medium text-slate-600">{t("ops.scan_audit.sections.windows")}</p>
        <div className="flex flex-col gap-2 md:flex-row">
          <Stat label={t("ops.scan_audit.window_labels.total")} value={w.audit_total} testId={`${dataTestId}-total-all`} />
          <Stat label={t("ops.scan_audit.window_labels.last_7d")} value={w.audit_7d} testId={`${dataTestId}-total-7d`} />
          <Stat label={t("ops.scan_audit.window_labels.last_24h")} value={w.audit_24h} testId={`${dataTestId}-total-24h`} />
        </div>
      </section>

      <section>
        <p className="mb-1 text-[11px] font-medium text-slate-600">{t("ops.scan_audit.sections.pre_lookup_windows")}</p>
        <div className="flex flex-col gap-2 md:flex-row">
          <Stat label={t("ops.scan_audit.window_labels.total")} value={w.pre_lookup_total} testId={`${dataTestId}-pre-total`} />
          <Stat label={t("ops.scan_audit.window_labels.last_7d")} value={w.pre_lookup_7d} testId={`${dataTestId}-pre-7d`} />
          <Stat label={t("ops.scan_audit.window_labels.last_24h")} value={w.pre_lookup_24h} testId={`${dataTestId}-pre-24h`} />
        </div>
      </section>

      <section>
        <p className="mb-1 text-[11px] font-medium text-slate-600">{t("ops.scan_audit.sections.audit_flags_lifetime")}</p>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-full bg-slate-100 px-2 py-0.5">rate_limit_triggered: <code>{a.rate_limit_triggered_count ?? 0}</code></span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">cross_org_override_used: <code>{a.cross_org_override_used_count ?? 0}</code></span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">denied_cross_org: <code>{a.denied_cross_org_count ?? 0}</code></span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">denied_cross_shop: <code>{a.denied_cross_shop_count ?? 0}</code></span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">minor_flag: <code>{a.minor_flag_count ?? 0}</code></span>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TopList title={t("ops.scan_audit.toplists.by_result")} items={a.by_result} testId={`${dataTestId}-by-result`} />
        <TopList title={t("ops.scan_audit.toplists.by_kind")} items={a.by_kind} testId={`${dataTestId}-by-kind`} />
        <TopList title={t("ops.scan_audit.toplists.top_scanners_opaque")} items={a.top_scanners} testId={`${dataTestId}-top-scanners`} />
        <TopList title={t("ops.scan_audit.toplists.top_organisation_ids")} items={a.top_organisation_ids} testId={`${dataTestId}-top-orgs`} />
        <TopList title={t("ops.scan_audit.toplists.by_deny_reason_category")} items={a.by_deny_reason_category} testId={`${dataTestId}-by-deny`} />
        <TopList title={t("ops.scan_audit.toplists.pre_lookup_top_scanners")} items={p.top_scanners} testId={`${dataTestId}-pre-top-scanners`} />
      </section>

      <section data-testid={`${dataTestId}-health`} className="flex flex-wrap gap-2 text-[11px]">
        <span className={`rounded-full px-2 py-0.5 ${h.has_rate_limit_activity ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>has_rate_limit_activity: <code>{String(h.has_rate_limit_activity)}</code></span>
        <span className={`rounded-full px-2 py-0.5 ${h.has_pre_lookup_denials ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>has_pre_lookup_denials: <code>{String(h.has_pre_lookup_denials)}</code></span>
        <span className={`rounded-full px-2 py-0.5 ${h.has_cross_org_activity ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>has_cross_org_activity: <code>{String(h.has_cross_org_activity)}</code></span>
        <span className={`rounded-full px-2 py-0.5 ${h.has_cross_shop_denials ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"}`}>has_cross_shop_denials: <code>{String(h.has_cross_shop_denials)}</code></span>
        <span className={`rounded-full px-2 py-0.5 ${h.has_minor_flags ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"}`}>has_minor_flags: <code>{String(h.has_minor_flags)}</code></span>
      </section>
    </div>
  );
};

export default ScanAuditOverviewCard;
